import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { CalendarIcon, Search, User } from "lucide-react";
import { cn } from "@/lib/utils";

interface CarePlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function CarePlanDialog({ open, onOpenChange, onSuccess }: CarePlanDialogProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [diagnosis, setDiagnosis] = useState("");
  const [memberId, setMemberId] = useState("");
  const [status, setStatus] = useState<"draft" | "active">("active");
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [targetEndDate, setTargetEndDate] = useState<Date | undefined>();
  const [patientSearch, setPatientSearch] = useState("");

  // Fetch patients from intake_forms
  const { data: patients } = useQuery({
    queryKey: ["patients-for-care-plan"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("patients")
        .select("user_id, first_name, last_name")
        .order("last_name");
      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  const filteredPatients = patients?.filter(p =>
    `${p.first_name} ${p.last_name}`.toLowerCase().includes(patientSearch.toLowerCase())
  );

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Not authenticated");
      if (!memberId) throw new Error("Please select a patient");
      if (!title) throw new Error("Please enter a title");

      const { error } = await supabase.from("care_plans").insert({
        member_id: memberId,
        provider_id: user.id,
        title,
        description: description || null,
        diagnosis: diagnosis || null,
        status,
        start_date: format(startDate, "yyyy-MM-dd"),
        target_end_date: targetEndDate ? format(targetEndDate, "yyyy-MM-dd") : null,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Care plan created successfully" });
      onSuccess();
      onOpenChange(false);
      resetForm();
    },
    onError: (error) => {
      toast({ title: "Error creating care plan", description: error.message, variant: "destructive" });
    },
  });

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setDiagnosis("");
    setMemberId("");
    setStatus("active");
    setStartDate(new Date());
    setTargetEndDate(undefined);
    setPatientSearch("");
  };

  const selectedPatient = patients?.find(p => p.user_id === memberId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create New Care Plan</DialogTitle>
          <DialogDescription>
            Create a treatment plan with goals and progress tracking for a patient
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Patient Selection */}
          <div className="space-y-2">
            <Label>Patient *</Label>
            {selectedPatient ? (
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">
                    {selectedPatient.first_name} {selectedPatient.last_name}
                  </span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setMemberId("")}>
                  Change
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search patients..."
                    value={patientSearch}
                    onChange={(e) => setPatientSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                {patientSearch && filteredPatients && filteredPatients.length > 0 && (
                  <div className="max-h-40 overflow-y-auto rounded-lg border">
                    {filteredPatients.map(patient => (
                      <button
                        key={patient.user_id}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors"
                        onClick={() => {
                          setMemberId(patient.user_id);
                          setPatientSearch("");
                        }}
                      >
                        {patient.first_name} {patient.last_name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Plan Title *</Label>
            <Input
              id="title"
              placeholder="e.g., Diabetes Management Plan"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Diagnosis */}
          <div className="space-y-2">
            <Label htmlFor="diagnosis">Diagnosis</Label>
            <Input
              id="diagnosis"
              placeholder="e.g., Type 2 Diabetes Mellitus"
              value={diagnosis}
              onChange={(e) => setDiagnosis(e.target.value)}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Describe the care plan objectives..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label>Initial Status</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as "draft" | "active")}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="active">Active</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(startDate, "MMM d, yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => date && setStartDate(date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Target End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !targetEndDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {targetEndDate ? format(targetEndDate, "MMM d, yyyy") : "Optional"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={targetEndDate}
                    onSelect={setTargetEndDate}
                    initialFocus
                    disabled={(date) => date < startDate}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending || !memberId || !title}
          >
            {createMutation.isPending ? "Creating..." : "Create Care Plan"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
